import React from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface ActiveSession {
  id: number
  agent: AgentRef
  started_at: string
  web_url: string
}

export interface Task {
  id: number
  summary: string
  instructions: string | null
  results: string | null
  status: string
  status_color: string
  status_label: string
  position: number
  assignee: AgentRef | null
  requestor: AgentRef
  reviewer: AgentRef | null
  parent_id: number | null
  active_session: ActiveSession | null
  created_at: string
  updated_at: string
}

interface BoardInfo {
  name: string
  color: string | null
}

interface ProjectInfo {
  name: string
  color: string | null
}

interface Props {
  task: Task
  onClick: () => void
  board?: BoardInfo
  project?: ProjectInfo
}

export function TaskCard({ task, onClick, board, project }: Props) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        position: 'relative',
        '&:hover': {
          borderColor: 'text.secondary',
          backgroundColor: 'grey.50',
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          mb: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {task.summary}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {project && (
          <Chip
            label={project.name}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: project.color || '#6B7280',
              color: '#fff',
              '& .MuiChip-label': { px: '6px', lineHeight: 1 },
            }}
          />
        )}
        {board && (
          <Chip
            label={board.name}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: board.color || '#6B7280',
              color: '#fff',
              '& .MuiChip-label': { px: '6px', lineHeight: 1 },
            }}
          />
        )}
        {task.assignee ? (
          <Chip
            label={task.assignee.username}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: '6px', lineHeight: 1 } }}
          />
        ) : (
          <Chip
            label="Unassigned"
            size="small"
            variant="outlined"
            color="default"
            sx={{ height: 20, fontSize: '0.7rem', fontStyle: 'italic', '& .MuiChip-label': { px: '6px', lineHeight: 1 } }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {new Date(task.updated_at).toLocaleDateString()}
        </Typography>
      </Box>
      {task.active_session && (
        <Link
          href={task.active_session.web_url}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            fontSize: '0.75rem',
            color: '#10B981',
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#10B981',
              flexShrink: 0,
              '@keyframes taskCardPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(1.3)' },
              },
              animation: 'taskCardPulse 2s ease-in-out infinite',
            }}
          />
          Live Session
        </Link>
      )}
    </Paper>
  )
}
